import React, { useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import "./SalesPageNew.css";

const S3_VIDEO = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/video';

const agentSections = [
   {
      key: 'design',
      tagKey: 'designTag',
      titleMainKey: 'designTitleMain',
      titleAccentKey: 'designTitleAccent',
      descKey: 'designDesc',
      video: `${S3_VIDEO}/design-agent.mp4`,
      accentColor: '#E8872B',
      bgTint: 'rgba(255, 200, 150, 0.25)',
   },
   {
      key: 'marketing',
      tagKey: 'marketingTag',
      titleMainKey: 'marketingTitleMain',
      titleAccentKey: 'marketingTitleAccent',
      descKey: 'marketingDesc',
      video: `${S3_VIDEO}/marketing-agent.mp4`,
      accentColor: '#8B7FC7',
      bgTint: 'rgba(160, 140, 220, 0.18)',
   },
   {
      key: 'sales',
      tagKey: 'salesTag',
      titleMainKey: 'salesTitleMain',
      titleAccentKey: 'salesTitleAccent',
      descKey: 'salesDesc',
      video: `${S3_VIDEO}/sales-agent.mp4`,
      accentColor: '#D64B8A',
      bgTint: 'rgba(220, 130, 180, 0.18)',
   },
   {
      key: 'customerService',
      tagKey: 'customerServiceTag',
      titleMainKey: 'customerServiceTitleMain',
      titleAccentKey: 'customerServiceTitleAccent',
      descKey: 'customerServiceDesc',
      video: `${S3_VIDEO}/customer-service-agent.mp4`,
      accentColor: '#E8872B',
      bgTint: 'rgba(200, 200, 200, 0.2)',
   },
];

function AutoPlayVideo({ src }) {
   const ref = useRef(null);
   useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const io = new IntersectionObserver(
         entries => {
            entries.forEach(e => {
               if (e.isIntersecting) el.play().catch(() => {});
               else el.pause();
            });
         },
         { threshold: 0.4 },
      );
      io.observe(el);
      return () => io.disconnect();
   }, []);

   return (
      <video ref={ref} src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
         muted loop playsInline preload="metadata" />
   );
}

export default function AgentFeatures({ windowWidth }) {
   const { t } = useTranslation();
   const isMobile = windowWidth < 768;

   return (
      <section style={{ maxWidth: 1152, margin: '0 auto', padding: '96px 24px' }}>
         <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 80 : 120 }}>
            {agentSections.map(agent => (
               <div key={agent.key} style={{ textAlign: 'center' }}>
                  {/* Eyebrow tag */}
                  <p style={{
                     fontSize: 15,
                     fontWeight: 600,
                     color: agent.accentColor,
                     textTransform: 'none',
                     marginBottom: 16,
                     letterSpacing: '0.02em',
                  }}>
                     {t(`salesPageNew.agentFeatures.${agent.tagKey}`)}
                  </p>

                  {/* Headline */}
                  <h2 className="sp-normal-case" style={{
                     fontSize: isMobile ? 36 : 52,
                     fontWeight: 700,
                     color: 'var(--sp-ink)',
                     letterSpacing: '-0.03em',
                     lineHeight: 1.1,
                     maxWidth: 800,
                     margin: '0 auto',
                     textTransform: 'none',
                  }}>
                     {t(`salesPageNew.agentFeatures.${agent.titleMainKey}`)}{' '}
                     <span style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: 'italic',
                        fontWeight: 400,
                        color: agent.accentColor,
                     }}>
                        {t(`salesPageNew.agentFeatures.${agent.titleAccentKey}`)}
                     </span>
                  </h2>

                  {/* Description */}
                  <p style={{
                     marginTop: 20,
                     color: 'var(--sp-text-soft)',
                     fontSize: isMobile ? 16 : 18,
                     lineHeight: 1.7,
                     maxWidth: 680,
                     margin: '20px auto 0',
                     textTransform: 'none',
                     fontWeight: 400,
                  }}>
                     {t(`salesPageNew.agentFeatures.${agent.descKey}`)}
                  </p>

                  {/* Video container */}
                  <div style={{
                     marginTop: 40,
                     background: agent.bgTint,
                     borderRadius: 24,
                     padding: isMobile ? 16 : 32,
                     maxWidth: 960,
                     margin: '40px auto 0',
                  }}>
                     <div style={{
                        borderRadius: 16,
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px -20px rgba(0,0,0,0.15)',
                     }}>
                        <AutoPlayVideo src={agent.video} />
                     </div>
                  </div>
               </div>
            ))}
         </div>
      </section>
   );
}
